/**
 * First-party cookie tracking (#164) — operator UI.
 *
 * Composition:
 *   - Header with the feature intent and a KPI strip (profiles enabled,
 *     rotation cadence range, 7+ day persistence rate across visitors).
 *   - Profiles list card: each profile shows its cookie name, domain,
 *     rotation schedule, ITP-safe mode, and a Set-Cookie header preview
 *     the engineer can paste into their edge worker.
 *   - Create profile card: a guided form covering cookie_name, domain,
 *     rotation_days, max_age_days, SameSite, Secure / HttpOnly, ITP-safe.
 *   - Visit simulator card: pick a profile, optionally supply an existing
 *     cookie value, POST /visit, inspect the rotated/issued/persisted_7d
 *     outcome plus the exact Set-Cookie the edge would emit.
 *
 * Brand rules: emerald->sky headline gradient, zinc borders, 6-8px radii,
 * light theme only, no emojis, no competitor names.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createFirstPartyCookieProfile,
	createProfileFormSchema,
	deleteFirstPartyCookieProfile,
	fetchFirstPartyCookieMetrics,
	listFirstPartyCookieProfiles,
	persistedPercent,
	previewSetCookieHeader,
	rotationSummary,
	SAME_SITE_VALUES,
	simulateFirstPartyCookieVisit,
	updateFirstPartyCookieProfile,
	type CreateProfileFormInput,
	type Metrics,
	type ProfileView,
	type SameSite,
	type VisitResponse,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — mirror the dash brand rules.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const SELECT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-red-500 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60";
const KBD =
	"rounded-[4px] border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 font-mono text-[11px] text-zinc-700";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const queryKeys = {
	profiles: () => ["first-party-cookie", "profiles"] as const,
	metrics: (id: string) => ["first-party-cookie", "metrics", id] as const,
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface FirstPartyCookiePageProps {
	initialProfileId?: string;
}

export function FirstPartyCookiePage(props: FirstPartyCookiePageProps = {}) {
	const queryClient = useQueryClient();
	const [selectedId, setSelectedId] = useState<string | null>(
		props.initialProfileId ?? null,
	);

	const listQuery = useQuery<ProfileView[]>({
		queryKey: queryKeys.profiles(),
		queryFn: listFirstPartyCookieProfiles,
	});

	const profiles = useMemo<ProfileView[]>(
		() => listQuery.data ?? [],
		[listQuery.data],
	);

	// Auto-select the first profile if the caller didn't pin one.
	useEffect(() => {
		if (selectedId === null && profiles.length > 0 && profiles[0]) {
			setSelectedId(profiles[0].id);
		}
		if (
			selectedId !== null &&
			profiles.length > 0 &&
			!profiles.some((p) => p.id === selectedId)
		) {
			setSelectedId(profiles[0]?.id ?? null);
		}
	}, [profiles, selectedId]);

	const selectedProfile = useMemo(
		() => profiles.find((p) => p.id === selectedId) ?? null,
		[profiles, selectedId],
	);

	const metricsQuery = useQuery<Metrics>({
		queryKey: queryKeys.metrics(selectedId ?? ""),
		queryFn: () => fetchFirstPartyCookieMetrics(selectedId as string),
		enabled: selectedId !== null,
	});

	const createMutation = useMutation({
		mutationFn: createFirstPartyCookieProfile,
		onSuccess: (profile) => {
			toast.success(`Profile "${profile.name}" created.`);
			setSelectedId(profile.id);
			queryClient.invalidateQueries({ queryKey: queryKeys.profiles() });
		},
		onError: (err: Error) =>
			toast.error(err.message ?? "Failed to create profile."),
	});

	const toggleMutation = useMutation({
		mutationFn: async (profile: ProfileView) =>
			updateFirstPartyCookieProfile(profile.id, { enabled: !profile.enabled }),
		onSuccess: (profile) => {
			toast.success(`Profile ${profile.enabled ? "enabled" : "disabled"}.`);
			queryClient.invalidateQueries({ queryKey: queryKeys.profiles() });
		},
		onError: (err: Error) => toast.error(err.message ?? "Toggle failed."),
	});

	const deleteMutation = useMutation({
		mutationFn: deleteFirstPartyCookieProfile,
		onSuccess: () => {
			toast.success("Profile removed.");
			queryClient.invalidateQueries({ queryKey: queryKeys.profiles() });
		},
		onError: (err: Error) => toast.error(err.message ?? "Delete failed."),
	});

	const visitMutation = useMutation({
		mutationFn: async (input: { id: string; cookieValue?: string }) =>
			simulateFirstPartyCookieVisit(input.id, input.cookieValue),
		onSuccess: (_data, vars) => {
			queryClient.invalidateQueries({
				queryKey: queryKeys.metrics(vars.id),
			});
		},
		onError: (err: Error) =>
			toast.error(err.message ?? "Visit simulation failed."),
	});

	const busy =
		createMutation.isPending ||
		deleteMutation.isPending ||
		toggleMutation.isPending ||
		visitMutation.isPending;

	const handleDelete = useCallback(
		(profile: ProfileView) => {
			if (busy) return;
			if (typeof window !== "undefined") {
				const ok = window.confirm(
					`Delete profile "${profile.name}"? This also removes tracked visitor rows.`,
				);
				if (!ok) return;
			}
			deleteMutation.mutate(profile.id);
		},
		[busy, deleteMutation],
	);

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------
	return (
		<main
			data-testid="first-party-cookie-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<KpiStrip
				profiles={profiles}
				metrics={metricsQuery.data ?? null}
				loading={listQuery.isLoading}
			/>

			<ProfilesCard
				profiles={profiles}
				loading={listQuery.isLoading}
				error={listQuery.error?.message ?? null}
				selectedId={selectedId}
				setSelectedId={setSelectedId}
				busy={busy}
				onToggle={(p) => toggleMutation.mutate(p)}
				onDelete={handleDelete}
			/>

			<CreateProfileCard
				busy={busy}
				onSubmit={(input) => createMutation.mutate(input)}
			/>

			<VisitSimulatorCard
				profile={selectedProfile}
				busy={busy}
				result={visitMutation.data ?? null}
				onSubmit={(cookieValue) => {
					if (!selectedProfile) {
						toast.error("Pick a profile first.");
						return;
					}
					visitMutation.mutate({
						id: selectedProfile.id,
						cookieValue: cookieValue || undefined,
					});
				}}
			/>
		</main>
	);
}

export default FirstPartyCookiePage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="text-brand-gradient text-3xl font-semibold tracking-tight sm:text-4xl">
				First-party cookie
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Register a first-party cookie profile for each apex domain you track.
				The edge writes the cookie on a top-level document response,
				re-issues it before the 7 day ITP window expires, and keeps the same
				opaque id across rotations so attribution persists past the Safari
				ceiling.
			</p>
		</header>
	);
}

function KpiStrip(props: {
	profiles: ProfileView[];
	metrics: Metrics | null;
	loading: boolean;
}) {
	const enabled = props.profiles.filter((p) => p.enabled).length;
	const rotationRange = useMemo(() => {
		if (props.profiles.length === 0) return { min: 0, max: 0 };
		let min = Number.POSITIVE_INFINITY;
		let max = Number.NEGATIVE_INFINITY;
		for (const p of props.profiles) {
			if (p.rotation_days < min) min = p.rotation_days;
			if (p.rotation_days > max) max = p.rotation_days;
		}
		return { min, max };
	}, [props.profiles]);
	const rangeLabel =
		props.profiles.length === 0
			? "—"
			: rotationRange.min === rotationRange.max
				? `${rotationRange.min}d`
				: `${rotationRange.min}–${rotationRange.max}d`;
	const persisted = props.metrics ? persistedPercent(props.metrics) : 0;
	const persistedLabel = props.metrics ? `${persisted}%` : "—";
	const cells = [
		{ label: "Profiles", value: String(props.profiles.length) },
		{ label: "Enabled", value: String(enabled) },
		{ label: "Rotation range", value: rangeLabel },
		{ label: "7+ day persistence", value: persistedLabel },
	];
	return (
		<section
			aria-label="First-party cookie statistics"
			className="grid grid-cols-2 gap-3 sm:grid-cols-4"
			data-testid="kpi-strip"
		>
			{cells.map((c) => (
				<div key={c.label} className={`${CARD} flex flex-col gap-1 p-4`}>
					<p className="text-xs font-medium uppercase tracking-wide text-zinc-500">
						{c.label}
					</p>
					<p className="text-2xl font-semibold tabular-nums text-zinc-900">
						{props.loading ? "—" : c.value}
					</p>
				</div>
			))}
		</section>
	);
}

function ProfilesCard(props: {
	profiles: ProfileView[];
	loading: boolean;
	error: string | null;
	selectedId: string | null;
	setSelectedId: (id: string | null) => void;
	busy: boolean;
	onToggle: (p: ProfileView) => void;
	onDelete: (p: ProfileView) => void;
}) {
	return (
		<section
			aria-labelledby="profiles-heading"
			className={`${CARD} flex flex-col gap-4 p-6`}
			data-testid="profiles-card"
		>
			<div className="flex items-baseline justify-between gap-3">
				<h2
					id="profiles-heading"
					className="text-lg font-semibold text-zinc-900"
				>
					Cookie profiles
				</h2>
				<p className="text-xs text-zinc-500">
					ITP-safe mode emits Set-Cookie only on top-level document
					navigations.
				</p>
			</div>

			{props.loading ? (
				<p className="text-sm text-zinc-500">Loading profiles…</p>
			) : props.error ? (
				<p className="text-sm text-red-600">{props.error}</p>
			) : props.profiles.length === 0 ? (
				<p className="text-sm text-zinc-500">
					No profiles yet. Create one below to start writing first-party
					cookies from the edge.
				</p>
			) : (
				<ul className="flex flex-col gap-3">
					{props.profiles.map((profile) => {
						const active = profile.id === props.selectedId;
						return (
							<li
								key={profile.id}
								className={`rounded-[8px] border ${
									active
										? "border-emerald-500 bg-emerald-50/40"
										: "border-zinc-200 bg-white"
								} p-4`}
								data-testid={`profile-row-${profile.id}`}
							>
								<div className="flex flex-wrap items-baseline justify-between gap-3">
									<div className="flex flex-col gap-1">
										<div className="flex items-baseline gap-2">
											<p className="text-sm font-semibold text-zinc-900">
												{profile.name}
											</p>
											<span className={KBD}>
												{profile.cookie_name}
											</span>
											{!profile.enabled ? (
												<span className="rounded-[4px] border border-zinc-200 bg-zinc-100 px-1.5 py-0.5 text-[11px] font-medium text-zinc-600">
													Disabled
												</span>
											) : null}
											{profile.itp_safe_mode ? (
												<span className="rounded-[4px] border border-sky-200 bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-700">
													ITP-safe
												</span>
											) : null}
										</div>
										<p className="text-xs text-zinc-500">
											Domain {profile.domain} · SameSite={profile.same_site}
											{profile.secure ? " · Secure" : ""}
											{profile.http_only ? " · HttpOnly" : ""}
										</p>
										<p className="text-xs text-zinc-500">
											{rotationSummary(profile)}
										</p>
									</div>
									<div className="flex items-center gap-2">
										<button
											type="button"
											className={BTN_NEUTRAL}
											onClick={() => props.setSelectedId(profile.id)}
											disabled={props.busy}
											data-testid={`select-profile-${profile.id}`}
										>
											{active ? "Selected" : "Select"}
										</button>
										<button
											type="button"
											className={BTN_NEUTRAL}
											onClick={() => props.onToggle(profile)}
											disabled={props.busy}
											data-testid={`toggle-profile-${profile.id}`}
										>
											{profile.enabled ? "Disable" : "Enable"}
										</button>
										<button
											type="button"
											className={BTN_DANGER}
											onClick={() => props.onDelete(profile)}
											disabled={props.busy}
											data-testid={`delete-profile-${profile.id}`}
										>
											Delete
										</button>
									</div>
								</div>

								<div className="mt-3 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3">
									<p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
										Set-Cookie preview
									</p>
									<p className="mt-1 break-all font-mono text-xs text-zinc-700">
										{previewSetCookieHeader(profile)}
									</p>
								</div>
							</li>
						);
					})}
				</ul>
			)}
		</section>
	);
}

// ---------------------------------------------------------------------------
// Create profile card
// ---------------------------------------------------------------------------

type FormState = {
	name: string;
	cookie_name: string;
	domain: string;
	max_age_days: string;
	rotation_days: string;
	same_site: SameSite;
	secure: boolean;
	http_only: boolean;
	itp_safe_mode: boolean;
};

const INITIAL_FORM: FormState = {
	name: "",
	cookie_name: "_tk_vid",
	domain: "",
	max_age_days: "365",
	rotation_days: "6",
	same_site: "Lax",
	secure: true,
	http_only: false,
	itp_safe_mode: true,
};

function CreateProfileCard(props: {
	busy: boolean;
	onSubmit: (input: {
		name: string;
		cookie_name: string;
		domain: string;
		max_age_days: number;
		rotation_days: number;
		same_site: SameSite;
		secure: boolean;
		http_only: boolean;
		itp_safe_mode: boolean;
		enabled: boolean;
	}) => void;
}) {
	const [form, setForm] = useState<FormState>(INITIAL_FORM);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const handleChange = <K extends keyof FormState>(k: K, v: FormState[K]) => {
		setForm((prev) => ({ ...prev, [k]: v }));
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (props.busy) return;
		const input: CreateProfileFormInput = {
			name: form.name,
			cookie_name: form.cookie_name,
			domain: form.domain,
			max_age_days: form.max_age_days,
			rotation_days: form.rotation_days,
			same_site: form.same_site,
			secure: form.secure,
			http_only: form.http_only,
			itp_safe_mode: form.itp_safe_mode,
			enabled: true,
		};
		const parsed = createProfileFormSchema.safeParse(input);
		if (!parsed.success) {
			setErrors(parsed.errors);
			return;
		}
		setErrors({});
		props.onSubmit({
			name: parsed.data.name,
			cookie_name: parsed.data.cookie_name,
			domain: parsed.data.domain,
			max_age_days: parsed.data.max_age_days ?? 365,
			rotation_days: parsed.data.rotation_days ?? 6,
			same_site: parsed.data.same_site ?? "Lax",
			secure: parsed.data.secure ?? true,
			http_only: parsed.data.http_only ?? false,
			itp_safe_mode: parsed.data.itp_safe_mode ?? true,
			enabled: parsed.data.enabled ?? true,
		});
		setForm(INITIAL_FORM);
	};

	return (
		<section
			aria-labelledby="create-heading"
			className={`${CARD} flex flex-col gap-4 p-6`}
			data-testid="create-profile-card"
		>
			<div className="flex flex-col gap-1">
				<h2
					id="create-heading"
					className="text-lg font-semibold text-zinc-900"
				>
					Create profile
				</h2>
				<p className="text-xs text-zinc-500">
					Rotation must be strictly less than max age. SameSite=None requires
					Secure.
				</p>
			</div>
			<form
				onSubmit={handleSubmit}
				className="grid grid-cols-1 gap-4 sm:grid-cols-2"
				data-testid="create-profile-form"
			>
				<Field label="Profile name" error={errors.name}>
					<input
						className={INPUT}
						value={form.name}
						onChange={(e) => handleChange("name", e.target.value)}
						placeholder="Shop main domain"
						data-testid="input-name"
					/>
				</Field>
				<Field label="Cookie name" error={errors.cookie_name}>
					<input
						className={INPUT}
						value={form.cookie_name}
						onChange={(e) => handleChange("cookie_name", e.target.value)}
						placeholder="_tk_vid"
						data-testid="input-cookie-name"
					/>
				</Field>
				<Field label="Cookie domain" error={errors.domain}>
					<input
						className={INPUT}
						value={form.domain}
						onChange={(e) => handleChange("domain", e.target.value)}
						placeholder="shop.example.com"
						data-testid="input-domain"
					/>
				</Field>
				<Field label="Max age (days)" error={errors.max_age_days}>
					<input
						className={INPUT}
						value={form.max_age_days}
						inputMode="numeric"
						onChange={(e) => handleChange("max_age_days", e.target.value)}
						data-testid="input-max-age"
					/>
				</Field>
				<Field label="Rotation (days)" error={errors.rotation_days}>
					<input
						className={INPUT}
						value={form.rotation_days}
						inputMode="numeric"
						onChange={(e) => handleChange("rotation_days", e.target.value)}
						data-testid="input-rotation"
					/>
				</Field>
				<Field label="SameSite">
					<select
						className={SELECT}
						value={form.same_site}
						onChange={(e) =>
							handleChange("same_site", e.target.value as SameSite)
						}
						data-testid="input-same-site"
					>
						{SAME_SITE_VALUES.map((v) => (
							<option key={v} value={v}>
								{v}
							</option>
						))}
					</select>
				</Field>
				<div className="sm:col-span-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
					<Toggle
						label="Secure"
						checked={form.secure}
						onChange={(v) => handleChange("secure", v)}
						testId="toggle-secure"
					/>
					<Toggle
						label="HttpOnly"
						checked={form.http_only}
						onChange={(v) => handleChange("http_only", v)}
						testId="toggle-http-only"
					/>
					<Toggle
						label="ITP-safe mode"
						checked={form.itp_safe_mode}
						onChange={(v) => handleChange("itp_safe_mode", v)}
						testId="toggle-itp"
					/>
				</div>
				{errors.secure ? (
					<p className="sm:col-span-2 text-xs text-red-600">{errors.secure}</p>
				) : null}
				<div className="sm:col-span-2 flex justify-end gap-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={props.busy}
						data-testid="submit-create"
					>
						{props.busy ? "Saving…" : "Create profile"}
					</button>
				</div>
			</form>
		</section>
	);
}

function Field(props: {
	label: string;
	error?: string | undefined;
	children: React.ReactNode;
}) {
	return (
		<label className="flex flex-col gap-1">
			<span className={LABEL}>{props.label}</span>
			{props.children}
			{props.error ? (
				<span className="text-xs text-red-600">{props.error}</span>
			) : null}
		</label>
	);
}

function Toggle(props: {
	label: string;
	checked: boolean;
	onChange: (v: boolean) => void;
	testId: string;
}) {
	return (
		<label className="flex items-center gap-2 rounded-[6px] border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700">
			<input
				type="checkbox"
				className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
				checked={props.checked}
				onChange={(e) => props.onChange(e.target.checked)}
				data-testid={props.testId}
			/>
			<span>{props.label}</span>
		</label>
	);
}

// ---------------------------------------------------------------------------
// Visit simulator
// ---------------------------------------------------------------------------

function VisitSimulatorCard(props: {
	profile: ProfileView | null;
	busy: boolean;
	result: VisitResponse | null;
	onSubmit: (cookieValue: string) => void;
}) {
	const [cookieValue, setCookieValue] = useState("");

	return (
		<section
			aria-labelledby="simulator-heading"
			className={`${CARD} flex flex-col gap-4 p-6`}
			data-testid="visit-simulator-card"
		>
			<div className="flex flex-col gap-1">
				<h2
					id="simulator-heading"
					className="text-lg font-semibold text-zinc-900"
				>
					Visit simulator
				</h2>
				<p className="text-xs text-zinc-500">
					Fires POST /visit against the selected profile. Leave the cookie
					value blank to issue a brand new id; paste an existing one to test
					rotation.
				</p>
			</div>
			{props.profile === null ? (
				<p className="text-sm text-zinc-500">
					Select a profile above to enable simulation.
				</p>
			) : (
				<div className="flex flex-col gap-3">
					<p className="text-xs text-zinc-500">
						Target: <span className={KBD}>{props.profile.cookie_name}</span> on{" "}
						<span className={KBD}>{props.profile.domain}</span>
					</p>
					<div className="flex flex-col gap-1">
						<span className={LABEL}>Existing cookie value (optional)</span>
						<input
							className={INPUT}
							value={cookieValue}
							onChange={(e) => setCookieValue(e.target.value)}
							placeholder="leave blank to issue fresh"
							data-testid="input-visit-cookie"
						/>
					</div>
					<div className="flex justify-end">
						<button
							type="button"
							className={BTN_PRIMARY}
							disabled={props.busy}
							onClick={() => props.onSubmit(cookieValue.trim())}
							data-testid="submit-visit"
						>
							{props.busy ? "Running…" : "Run visit"}
						</button>
					</div>
					{props.result ? (
						<div
							className="rounded-[6px] border border-zinc-200 bg-zinc-50 p-3"
							data-testid="visit-result"
						>
							<p className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
								Outcome
							</p>
							<ul className="mt-2 flex flex-col gap-1 text-xs text-zinc-700">
								<li>
									<strong>{props.result.issued ? "Issued" : "Returning"}</strong>
									{props.result.rotated ? ", rotated" : ""},
									{" "}
									age {props.result.age_days}d,
									{" "}
									persisted 7d: {props.result.persisted_7d ? "yes" : "no"}
								</li>
								<li>
									Cookie value: <span className={KBD}>{props.result.cookie_value}</span>
								</li>
								<li>
									Set-Cookie:
									<br />
									<span className="mt-1 inline-block break-all font-mono text-[11px] text-zinc-700">
										{props.result.set_cookie ?? "(no-op this request)"}
									</span>
								</li>
							</ul>
						</div>
					) : null}
				</div>
			)}
		</section>
	);
}
